/** Mirror backend/utils/ticketAccess.js — portal ticket queue permissions */

import { isSuperAdminPortalView } from './hrAccess';

// HR Ops owns both HR and Facilities queues; HR Admin has no queue of its own
// The six products sit in the IT queue — the tech team handles them.
/*
 * Products no longer offered when raising or moving a ticket.
 *
 * 'Recon Radar' was one option for two products, Radar and Recon. They are now
 * separate. The old name stays here rather than being deleted: tickets raised
 * under it exist, nobody can tell which of the two each one meant, and the
 * people who handle those tickets still have to see and update them.
 */
export const RETIRED_PRODUCT_CATEGORIES = ['Recon Radar'];

/* Every product category, retired included — this decides visibility and who
   may be assigned, and a retired product is still a product. */
export const PRODUCT_CATEGORIES = ['Tychi GL', 'Tychi Vault', 'Tychi Master', 'Radar', 'Recon', 'Allocator', 'Tychi Reports', ...RETIRED_PRODUCT_CATEGORIES];

/** The products a new ticket can be raised against, or moved into. */
export const ACTIVE_PRODUCT_CATEGORIES = PRODUCT_CATEGORIES.filter(
  (c) => !RETIRED_PRODUCT_CATEGORIES.includes(c)
);

/** True when a ticket belongs to one of the six products. */
export const isProductCategory = (category) =>
  PRODUCT_CATEGORIES.includes(String(category || '').trim());

const TICKET_CATEGORIES_BY_ROLE = {
  'IT ADMIN': ['IT Support', ...PRODUCT_CATEGORIES],
  'IT Support': ['IT Support', ...PRODUCT_CATEGORIES],
  HR: ['HR', 'Facilities'],
  'Hr Operation': ['HR', 'Facilities'],
  FINANCE: ['Admin'],
  'Fundtec Finance': ['Admin'],
  Finance: ['Admin'],
  'Super Admin': ['IT Support', 'HR', 'Facilities', 'Admin', ...PRODUCT_CATEGORIES],
};

/*
 * Every queue a ticket can live in, built from the role map so adding a product
 * or a department in one place is enough.
 *
 * Needed because ticketCategoriesForPortalUser returns null for an admin —
 * meaning "no restriction" — and a caller that wants an actual list has to turn
 * that into one rather than reading it as empty.
 */
export const ALL_TICKET_CATEGORIES = [...new Set(
  Object.values(TICKET_CATEGORIES_BY_ROLE).flat()
)];

export const ticketCategoriesForPortalUser = (user) => {
  if (!user) return null;
  if (user.role === 'admin' && user.login_type !== 'department') return null;
  const key = user.tychi_role || user.dept || user.department || '';
  const byRole = TICKET_CATEGORIES_BY_ROLE[key] || null;

  // Mirrors the backend: Product Team membership grants the product queues on
  // its own, because the developers have no portal role to grant them by.
  const isProductPerson = Number(user.is_product_team) === 1
    || Number(user.is_product_lead) === 1;
  if (!isProductPerson) return byRole;
  return [...new Set([...(byRole || []), ...PRODUCT_CATEGORIES])];
};


/**
 * The person who raised a ticket may reopen it when the problem comes back.
 *
 * Deliberately narrow: only from Resolved or Closed, only back to Open, only
 * their own ticket. They still cannot mark it In Progress or resolve it.
 *
 * Without this the only people who can say "it is not fixed" are the people
 * who fixed it, which is the wrong way round — and the alternative is the
 * reporter raising a duplicate, losing the history of a problem that has now
 * failed twice.
 */
export const canReopenOwnTicket = (userCtx, ticket, nextStatus) => {
  if (!userCtx || !ticket) return false;
  if (Number(ticket.raised_by) !== Number(userCtx.id)) return false;
  return ['Resolved', 'Closed'].includes(ticket.status) && nextStatus === 'Open';
};

/** HR / IT / Finance portal users can update status for tickets in their queue */
export const canUpdateTicketStatus = (user, ticket) => {
  if (!user || !ticket) return false;
  // Super Admin has full control over every queue
  if (isSuperAdminPortalView(user)) return true;
  if (user.role === 'admin' && user.login_type !== 'department') return true;
  const categories = ticketCategoriesForPortalUser(user);
  return Boolean(categories?.includes(ticket.category));
};

/**
 * Who may hand a ticket to somebody else.
 *
 * Admins and the Super Admin for anything; the Product Lead for product
 * tickets, which is the entire reason that role exists. It was missing here,
 * so the lead could watch product tickets arrive and had no way to give one
 * to anybody.
 *
 * `can` is passed in because the Product Lead is a permission rather than a
 * portal role, and permissions do not live on the user object. Called without
 * a ticket it answers "could this person assign anything at all".
 */
export const canAssignTicket = (user, ticket = null, can = null) => {
  if (!user) return false;
  if (isSuperAdminPortalView(user)) return true;
  if (user.role === 'admin' && user.login_type !== 'department') return true;

  if (can && can('bugs_triage')) {
    if (!ticket) return true;
    return isProductCategory(ticket.category);
  }
  return false;
};

/** Who can open the raise-ticket page (not All Tickets admin view) */
export const canRaiseTicket = (user, can) => {
  if (!user) return false;
  if (isSuperAdminPortalView(user)) return false;
  if (user.role === 'admin') return false;
  return typeof can === 'function' ? can('tickets') : false;
};
